import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { Booking } from "@/lib/services/bookings";
import Ionicons from "@react-native-vector-icons/ionicons";
import React from "react";
import { StyleSheet, View } from "react-native";

const PROGRESS_STEPS = [
  { key: "requested", label: "Requested" },
  { key: "assigned", label: "Assigned" },
  { key: "pickup", label: "Pickup" },
  { key: "delivered", label: "Delivered" },
  { key: "completed", label: "Completed" },
] as const;

const STATUS_STEP_INDEX: Record<string, number> = {
  pending: 0,
  quoted: 0,
  accepted: 1,
  scheduled: 1,
  arrived: 1,
  pickup_inspection: 2,
  in_progress: 2,
  delivered: 3,
  delivery_inspection: 3,
  completed: 4,
};

const getCurrentStepIndex = (booking: Booking) => {
  if (booking.status === "completed") {
    return PROGRESS_STEPS.length;
  }
  if (booking.status === "cancelled") {
    if (!booking.driver_id) return 0;
    if (!booking.started_at) return 1;
    return 2;
  }
  return STATUS_STEP_INDEX[booking.status] ?? 0;
};

interface BookingStatusProgressProps {
  booking: Booking;
}

export const BookingStatusProgress: React.FC<BookingStatusProgressProps> = ({
  booking,
}) => {
  const { theme } = useAuth();
  const currentIndex = getCurrentStepIndex(booking);
  const isCancelled = booking.status === "cancelled";

  return (
    <View style={[styles.container, { backgroundColor: theme.card }]}>
      <View style={styles.trackRow}>
        {PROGRESS_STEPS.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = isCancelled
            ? index === currentIndex
            : index === currentIndex && currentIndex < PROGRESS_STEPS.length;
          const isActive = isCompleted || isCurrent;
          const connectorActive = index < currentIndex;

          return (
            <React.Fragment key={step.key}>
              <View style={styles.stepColumn}>
                <View
                  style={[
                    styles.stepCircle,
                    isActive && styles.stepCircleActive,
                    isCurrent && styles.stepCircleCurrent,
                  ]}
                >
                  {isCompleted ? (
                    <Ionicons
                      name="checkmark"
                      size={14}
                      color={BrandColors.white}
                    />
                  ) : isCurrent ? (
                    <View style={styles.stepCurrentDot} />
                  ) : null}
                </View>
              </View>
              {index < PROGRESS_STEPS.length - 1 ? (
                <View
                  style={[
                    styles.connector,
                    connectorActive && styles.connectorActive,
                  ]}
                />
              ) : null}
            </React.Fragment>
          );
        })}
      </View>

      <View style={styles.labelsRow}>
        {PROGRESS_STEPS.map((step, index) => {
          const isCurrentLabel = isCancelled
            ? index === currentIndex
            : index === currentIndex && currentIndex < PROGRESS_STEPS.length;
          const label =
            isCancelled && index === currentIndex ? "Canceled" : step.label;

          return (
            <View key={step.key} style={styles.labelColumn}>
              <ThemedText
                style={[
                  styles.stepLabel,
                  isCurrentLabel && styles.stepLabelCurrent,
                  isCancelled &&
                    index === currentIndex &&
                    styles.stepLabelCanceled,
                ]}
                type="extraSmall"
                numberOfLines={1}
              >
                {label}
              </ThemedText>
            </View>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingVertical: 20,
    backgroundColor: BrandColors.white,
  },
  trackRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  stepColumn: {
    flex: 1,
    alignItems: "center",
  },
  stepCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: BrandColors.lightGray,
    backgroundColor: BrandColors.white,
    alignItems: "center",
    justifyContent: "center",
  },
  stepCircleActive: {
    borderColor: BrandColors.primary,
    backgroundColor: BrandColors.primary,
  },
  stepCircleCurrent: {
    borderColor: BrandColors.primary,
    backgroundColor: BrandColors.primary,
  },
  stepCurrentDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: BrandColors.white,
  },
  connector: {
    flex: 1,
    height: 2,
    backgroundColor: BrandColors.lightGray,
    marginHorizontal: -4,
    marginBottom: 0,
  },
  connectorActive: {
    backgroundColor: BrandColors.primary,
  },
  labelsRow: {
    flexDirection: "row",
    marginTop: 8,
  },
  labelColumn: {
    flex: 1,
    alignItems: "center",
  },
  stepLabel: {
    color: BrandColors.mediumGray,
    fontSize: 10,
    textAlign: "center",
  },
  stepLabelCurrent: {
    color: BrandColors.primary,
    fontWeight: "600",
  },
  stepLabelCanceled: {
    color: BrandColors.error,
    fontWeight: "600",
  },
});
